/**
 * PushNotificationsCard — UX section for managing Web Push notifications.
 *
 * Used in customer Profile, driver Profile, admin Settings.
 *
 * Distinguishes 6 states clearly:
 *   1) not_supported           → browser doesn't support Web Push at all
 *   2) ios_safari_normal       → iOS Safari NOT installed as PWA — install required
 *   3) ios_pwa_default         → iOS standalone PWA, permission default (can subscribe)
 *   4) ios_pwa_active          → iOS standalone PWA, subscribed (active)
 *   5) browser_active          → Desktop/Android browser subscribed (active)
 *   6) browser_default/denied  → Desktop/Android browser default/denied
 *
 * The card never claims "active" when the user is in a context that cannot
 * actually deliver real OS-level notifications.
 */
import { useState } from "react";
import { toast } from "sonner";
import {
  Bell, BellOff, Smartphone, ShieldAlert, CheckCircle2, Send,
  Share, Plus, HomeIcon, Apple,
} from "lucide-react";
import { usePushNotifications } from "@/lib/usePushNotifications";

export default function PushNotificationsCard() {
  const {
    supported, permission, subscribed, loading,
    isIOSDevice, isStandalone, iosNeedsPwa,
    subscribe, unsubscribe, sendTest,
  } = usePushNotifications();
  const [testing, setTesting] = useState(false);

  const onSubscribe = async () => {
    const res = await subscribe();
    if (res.ok) toast.success("Notifiche push attivate!");
    else if (res.reason === "denied") toast.error("Permesso negato dal browser.");
    else if (res.reason === "ios_pwa_required")
      toast.info("Su iPhone aggiungi DapNow alla schermata Home, poi riapri da lì.");
    else toast.error(`Impossibile attivare: ${res.reason || "errore"}`);
  };

  const onUnsubscribe = async () => {
    const res = await unsubscribe();
    if (res.ok) toast.success("Notifiche push disattivate.");
    else toast.error(`Errore: ${res.reason || ""}`);
  };

  const onTest = async () => {
    setTesting(true);
    const res = await sendTest();
    setTesting(false);
    if (res.ok) {
      toast.success(
        "Notifica test inviata. Per vederla come notifica di sistema, prova a chiudere o mettere in background questa scheda."
      );
    } else {
      toast.error(`Test fallito: ${res.error || res.data?.status || "verifica permessi"}`);
    }
  };

  // ---------------- Render branches ----------------

  // 1) Not supported by browser (and not iOS — iOS handled separately)
  if (!supported && !isIOSDevice) {
    return (
      <Card>
        <Header />
        <InfoBox color="amber">
          <ShieldAlert className="size-4 mt-0.5 shrink-0" />
          <span>
            Questo browser non supporta le notifiche push. Usa una versione aggiornata di
            Chrome, Firefox, Edge o Safari.
          </span>
        </InfoBox>
      </Card>
    );
  }

  // 2) iOS Safari normale (non-PWA) → installazione obbligatoria
  if (isIOSDevice && !isStandalone) {
    return (
      <Card>
        <Header />
        <InfoBox color="blue">
          <Smartphone className="size-5 mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold text-blue-950">
              Installa DapNow sulla schermata Home per ricevere notifiche
            </p>
            <p className="mt-1 text-blue-900/90">
              Su iPhone le notifiche push web funzionano <strong>solo se DapNow è installata
              come app</strong> dalla schermata Home (Apple consente push solo a PWA standalone).
            </p>
          </div>
        </InfoBox>

        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <Step n={1} icon={<Share className="size-4" />}>
            In Safari, tocca <strong>Condividi</strong> in basso
          </Step>
          <Step n={2} icon={<Plus className="size-4" />}>
            Scegli <strong>Aggiungi a schermata Home</strong>
          </Step>
          <Step n={3} icon={<HomeIcon className="size-4" />}>
            Apri DapNow dalla <strong>Home</strong> e attiva le push
          </Step>
        </div>

        <p className="mt-4 text-xs text-dapnow-muted flex items-center gap-1.5">
          <Apple className="size-3.5" />
          Richiede iOS 16.4 o più recente.
        </p>
      </Card>
    );
  }

  // 3) Blocked by user (any platform)
  if (permission === "denied" && !subscribed) {
    return (
      <Card>
        <Header />
        <InfoBox color="rose">
          <BellOff className="size-4 mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold">Notifiche bloccate</p>
            <p className="mt-1">
              {isIOSDevice
                ? "Vai in Impostazioni iPhone → Notifiche → DapNow → attiva 'Consenti notifiche', poi torna qui."
                : "Sblocca le notifiche dalle impostazioni del browser (icona lucchetto a sinistra dell'URL → Notifiche → Consenti), poi ricarica la pagina."}
            </p>
          </div>
        </InfoBox>
      </Card>
    );
  }

  // 4) Active (PWA iOS or Desktop/Android)
  if (subscribed) {
    return (
      <div
        data-testid="push-card"
        className="rounded-2xl border border-emerald-200 bg-emerald-50/70 p-5"
      >
        <Header active />

        <p className="mt-2 text-sm text-emerald-900 flex items-center gap-1.5">
          <CheckCircle2 className="size-4" />
          {isStandalone
            ? "Push attive · ricevi notifiche di sistema iPhone anche con app chiusa"
            : "Push attive · ricevi notifiche di sistema anche con tab chiusa"}
        </p>

        <div className="mt-3 rounded-xl border border-emerald-200/60 bg-white/60 px-3 py-2 text-xs text-emerald-900/80">
          💡 <strong>Per testare correttamente:</strong> dopo aver cliccato "Invia notifica test",
          {isStandalone
            ? " metti DapNow in background (premi Home) e attendi 1–3 secondi: la notifica deve comparire nel Centro Notifiche."
            : " chiudi o minimizza questa scheda e attendi 1–3 secondi: la notifica deve comparire come notifica di sistema del computer."}
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <button
            onClick={onTest}
            disabled={testing}
            data-testid="push-test-btn"
            className="h-10 px-4 rounded-xl bg-dapnow-blue text-white text-sm font-semibold hover:bg-dapnow-blue-hover transition disabled:opacity-60 flex items-center gap-1.5"
          >
            <Send className="size-4" /> {testing ? "Invio…" : "Invia notifica test"}
          </button>
          <button
            onClick={onUnsubscribe}
            disabled={loading}
            data-testid="push-unsubscribe-btn"
            className="h-10 px-4 rounded-xl border border-dapnow-line bg-white text-sm font-semibold hover:bg-dapnow-bg transition disabled:opacity-60"
          >
            Disattiva notifiche
          </button>
        </div>
      </div>
    );
  }

  // 5) Default state (can subscribe) — PWA iOS or Desktop/Android
  return (
    <Card>
      <Header />
      <p className="mt-2 text-sm text-dapnow-muted">
        Ricevi aggiornamenti operativi come notifiche di sistema:
        driver assegnato, pacco ritirato, consegna in arrivo. La ricezione, anche con DapNow
        in background, dipende dal dispositivo, dai permessi e dalla connessione.
        Puoi sempre consultare lo stato aggiornato nell’app.
        {isStandalone && (
          <span className="ml-1 text-emerald-700 font-medium">
            ✓ DapNow è installata come app
          </span>
        )}
      </p>
      <button
        onClick={onSubscribe}
        disabled={loading}
        data-testid="push-subscribe-btn"
        className="mt-4 h-11 px-5 rounded-xl bg-dapnow-blue text-white text-sm font-semibold hover:bg-dapnow-blue-hover transition disabled:opacity-60 flex items-center gap-1.5"
      >
        <Bell className="size-4" /> {loading ? "Attivazione…" : "Attiva notifiche"}
      </button>
    </Card>
  );
}

/* ------------------------- subcomponents ------------------------- */
function Card({ children }) {
  return (
    <div
      data-testid="push-card"
      className="rounded-2xl border border-dapnow-line bg-white p-5"
    >
      {children}
    </div>
  );
}

function Header({ active = false }) {
  return (
    <div className="flex items-center gap-2">
      <Bell className={`size-5 ${active ? "text-emerald-700" : "text-dapnow-blue"}`} />
      <h3 className="font-display text-lg font-bold tracking-tight">Notifiche push</h3>
    </div>
  );
}

function InfoBox({ children, color = "amber" }) {
  const palette = {
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    blue:  "bg-blue-50 border-blue-200 text-blue-900",
    rose:  "bg-rose-50 border-rose-200 text-rose-900",
  }[color];
  return (
    <div className={`mt-3 flex items-start gap-2 text-sm border rounded-xl p-3 ${palette}`}>
      {children}
    </div>
  );
}

function Step({ n, icon, children }) {
  return (
    <div className="rounded-xl border border-dapnow-line bg-white px-3 py-2 flex items-start gap-2 text-sm">
      <span className="size-6 shrink-0 rounded-full bg-dapnow-blue text-white text-xs font-bold flex items-center justify-center">
        {n}
      </span>
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="text-dapnow-blue">{icon}</span>
        <span className="text-dapnow-ink">{children}</span>
      </div>
    </div>
  );
}
